import AuthService from '../services/auth.service';
//import { createLocal } from 'the-storages';
import router from "@/router/index";
import emitter from "@/services/emitter.service";
import { Constants } from '@/cfg/constants';
import { getUdid } from '@/utils/guestSession';
import { getPendo, getPendoVisitorMetadata } from '@/services/pendo.service';

export const auth = {
  namespaced: true,
  state: {
    user: {},
    authorized: false,
    agreed: false,
    token: "",
    client_user: {},
    priceId: null,
    tutorialShown: true,
    passwordResetStatus: "",
    passwordChangeStatus: "",
    campaign_name: "",
    accountType: "",
    preAuthTosAccepted: false,
    purchasingPlanCode: "",
    purchaseType: 'plan',
    purchaseInitiated: false,
  },
  actions: {
    setAccountType({ commit }, accountType) {
      commit('setAccountType', accountType);
    },
    login({ commit, state }, user) {
      return AuthService.vanillaLogin(user).then(
        response => {
          commit('loginSuccess', { 
            email: user.email, 
            token: response.data.result.token,
            authorized: true
          }, true);
          commit('event/setSettings', false, { root: true });
          this.dispatch('auth/userMe', {
            token: response.data.result.token,
            room: false
          });
          // attempt saving campaign data here.  Still might not work logically.
          // send campaign_name to backend if it exists (not an empty string)
          const campaignDataProxy = {
            token: response.data.result.token,
            name: "campaign_name",
            value: state.campaign_name,
          };
          console.log("Value of campaignDataProxy: ", campaignDataProxy);
          if (campaignDataProxy.token && campaignDataProxy.value !== "") {
            AuthService.saveCampaignData(campaignDataProxy).then(
              response => {
                console.log("Campaign data saved successfully: ", response.data);
              },
              error => {
                console.error("Error saving campaign data: ", error);
              }
            );
            // trying to send the user to /plans here is a nogo.  It causes a race condition.
          }
          return Promise.resolve(user);
        },
        error => {
          commit('loginFailure');
          return Promise.reject(error);
        }
      );
    },
    logout({ commit }) {
        commit('logout')
        commit('event/setSettings', false, { root: true })
        commit('nonpersist/setUser', null, { root: true })
        commit('nonpersist/setPrefetched', true, { root: true })
        commit('nonpersist/setAsrTimeLeft', 0, { root: true })
    },
    register({ commit }, user) {
      return AuthService.vanillaRegister(user).then(
        response => {
          commit('registerSuccess', { 
            email: user.email, 
            name: user.name,
            email_verification_required: response.data.result.email_verification_required,
            authorized: false
          });
          commit('setToken', response.data.result.token);
          return Promise.resolve(response.data);
        },
        error => {
          commit('registerFailure', error);
          return Promise.reject(error);
        }
      );
    },
    googleLogin({ commit }, { googleResponse, room}) {
      return AuthService.googleLogin(googleResponse).then(
        response => {
            //console.log(response);
            if (!room) {
              commit('loginSuccess', {
                  authorized: true,
                }
              );
              commit('event/setSettings', false, { root: true });
            } else {
              commit('roomLoginSuccess', room);
              commit('event/setSettings', false, { root: true });
            }
            
            this.dispatch('auth/userMe', {
              token: response.data.result.token,
              room: room
            });
          return Promise.resolve(response.data);
        },
        error => {
          commit('registerFailure', error);
          return Promise.reject(error);
        }
      );
    },
    appleLogin({ commit }, { appleResponse, room}) {
      return AuthService.appleLogin(appleResponse).then(
        response => {
            //console.log(response);
            if (!room) {
              commit('loginSuccess', {
                  authorized: true,
                }
              );
              commit('event/setSettings', false, { root: true });
            } else {
              commit('roomLoginSuccess');
              commit('event/setSettings', false, { root: true });
            }
            this.dispatch('auth/userMe', {
              token: response.data.result.token,
              room: room
            });
          return Promise.resolve(response.data);
        },
        error => {
          commit('registerFailure');
          return Promise.reject(error);
        }
      );
    },
    userMe({ commit, rootState}, { token, room}) {
      return AuthService.userMe(token).then(
        response => {
          const attrs = response?.data?.result?.attributes || {};
          const termsAcceptedRaw = attrs.terms_accepted ?? attrs.term_accepted;
          const termsAccepted = termsAcceptedRaw === true || termsAcceptedRaw === 'true' || termsAcceptedRaw === 1 || termsAcceptedRaw === '1';

          if (response.data.result.is_activated) {
            commit('setAuthorized', true);
          }

          commit('setAgreedState', termsAccepted);

          // Backend leaves the language empty for freshly created accounts,
          // so the region default has to be persisted from here.
          const languageMissing = !response.data.result.language;
          if (languageMissing) {
            response.data.result.language = Constants.DEFAULT_LANGUAGE;
          }

          commit('nonpersist/setUser', response.data.result, { root: true });
          commit('setToken', token);
          commit('nonpersist/setPrefetched', true, { root: true});
          commit('nonpersist/setAsrTimeLeft', response.data.result.features.asr.left_allowed_items, { root: true});

          if (languageMissing) {
            AuthService.updateProfile({ language: Constants.DEFAULT_LANGUAGE }).catch(error => {
              console.error("Error saving default language: ", error);
            });
          }

            const shortLang = rootState.locale.locale ? rootState.locale.locale : "en";

            const pendo = getPendo(response.data.result);
            if (pendo) {
              pendo.identify({
                visitor: {
                  id: response.data.result.email,
                  ...getPendoVisitorMetadata(response.data.result, shortLang),
                },
                account: {}
              });
            }

          if (room) {
            router.go();
          }
          emitter.emit('pricing-page-on-signed-in');
          return Promise.resolve(response.data);
        },
        error => {
          this.dispatch("auth/logout");
          commit('nonpersist/setPrefetched', true, { root: true});

          return Promise.reject(error);
        }
      );
    },
    clearUserMe({ commit }) {
      commit('clearUser');
    },
    saveCampaignDataToBackend({commit}, user) {
      return AuthService.saveCampaignData(user).then(
        response => {
          commit('campaignRedirect');
          return Promise.resolve(response.data);
        },
        error => {
          return Promise.reject(error);
        }
      )
    },
    storeCampaignDataLocally({commit}, campaignName) {
      commit('setCampaignName', campaignName);
    },
    update({commit}, user) {
      const profile = { ...user, language: user.language || Constants.DEFAULT_LANGUAGE };

      return AuthService.updateProfile(profile).then(
        response => {
          commit('nonpersist/setUser', profile, { root: true });
          return Promise.resolve(response.data);
        },
        error => {
          
          return Promise.reject(error);
        }
      );
    },
    getResetPasswordVerifyCode({commit}, {email, code}) {
      return AuthService.resetPasswordVerifyCode(email, code).then(
        response => {
          commit('resetPasswordTokenCreated');
          return Promise.resolve(response);
        },
        error => {
          commit('resetPasswordTokenCreationFailure');
          return Promise.reject(error);
        }
      );
    },
    resetNewPassword({commit}, user) {
      return AuthService.setNewPassword(user).then(
        response => {
          commit('passwordResetSuccess');
          return Promise.resolve(response.data);
        },
        error => {
          commit('passwordResetFailure');
          return Promise.reject(error);
        }
      );
    },
    delete({commit}) {
        return AuthService.deleteAccount().then(
            response => {
                commit('logout');
                return Promise.resolve(response.data);
            },
            error => {

                return Promise.reject(error);
            }
        );
    },
    markForDelete({commit}, email) {
        return AuthService.markForDeletion(email).then(
            response => {
                commit('logout');
                return Promise.resolve(response.data);
            },
            error => {
                return Promise.reject(error);
            }
        );
    },
  },
  mutations: {
    loginSuccess(state, user) {
      state.authorized = true;
      state.client_user = user;
    },
    loginFailure(state) {
      state.authorized = false;
    },
    roomLoginSuccess(state) {
      state.authorized = true;
    },
    logout(state) {
      state.authorized = false;
      state.user = null;
      state.token = null;
      state.client_user = null;
      state.agreed = false;
      state.purchasingPlanCode = "";
    },
    clearUser(state) {
      state.user = {};
    },
    registerSuccess(state, userDetails) {
      state.authorized = false;
      state.client_user = userDetails;

    },
    registerFailure(state) {
      state.authorized = false;
    },
    passwordResetSuccess(state) {
      state.passwordResetStatus = "success";
    },
    passwordResetFailure(state) {
      state.passwordResetStatus = "failure";
    },
    setCampaignName(state, campaignName) {
      state.campaign_name = campaignName;
    },
    setAccountType(state, accountType) {
      state.accountType = accountType;
    },
    setPreAuthTosAccepted(state, accepted) {
      state.preAuthTosAccepted = accepted;
    },
    campaignRedirect() {
      router.push({ path: '/plans'});
    },
    setToken(state, token) {
      state.token = token;
    },
    setAuthorized(state, status) {
      state.authorized = status;
    },
    setAgreedState(state, agreed) {
      state.agreed = !!agreed;
    },
    setAgreed(state, agreed) {
        state.agreed = agreed;
        return AuthService.setUserAttribute({
            token: state.token,
            name: "terms_accepted",
            value: true,
        }).then(
            response => {
                return Promise.resolve(response.data);
            },
            error => {
                return Promise.reject(error);
            }
        )
    },
    setPriceId(state, priceId) {
        state.priceId = priceId;
    },
    resetPasswordStatus(state) {
      state.passwordResetStatus = "";
      state.passwordChangeStatus = "";
    },
    resetPasswordTokenCreated(state) {
      state.passwordTokenCreatedStatus = "success";
    },
    resetPasswordTokenCreatedFailure(state) {
      state.passwordTokenCreatedStatus = "failure";
    },
    passwordChangeSuccess(state) {
      state.passwordChangeStatus = "success";
    },
    passwordChangeFailure(state) {
      state.passwordChangeStatus = "failure";
    },
    setPurchasingPlan(state, code) {
        state.purchasingPlanCode = code;
    },
    setPurchaseType(state, purchaseType) {
        state.purchaseType = purchaseType;
    },
    setPurchaseInitiated(state, bool) {
        state.purchaseInitiate = bool;
    }
  },
  getters: {
    isAuthorized(state) {
      return state.authorized
    },
    token(state) {
        return state.token;
    },
    getPurchasingPlan(state) {
        return state.purchasingPlanCode;
    },
    udid() {
      return getUdid();
    }
  }
}